import {
  BonusContext,
  IBonusScene,
  IConfiguration,
  INumberFormatter,
  NUMBER_FORMATTER_TOKEN,
  SelectAction,
  SelectionArgs,
} from '@cgs/common';
import { BonusResponse, SpinResponse } from '@cgs/data-access';
import {
  BonusGameWithRules,
  BonusResponseWithRules,
  RulePropertyGenerator,
  RuleTypeLogic,
} from '@cgs/features';
import {
  GAME_DATA_SERVICE_TOKEN,
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  GameDataService,
  GameStateMachine,
  IGameStateMachineProvider,
} from '@cgs/machines';
import { CancellationToken, Func3 } from '@cgs/shared';
import { Container, SceneObject } from '@cgs/wge';

import { CorsicaBonusOrientationHandlerComponent } from './corsica-bonus-orientation-handler-component';

const enum ExplosionRound {
  Fail = 'FAIL',
  Boom = 'BOOM',
}

const enum BustedRound {
  Bust = 'BUSTED',
  RunAway = 'RUNAWAY',
}

const enum DirectionRound {
  Bust = 'BUSTED',
  Escaped = 'ESCAPED',
}

export class CorsicaBonusGame extends BonusGameWithRules {
  private readonly gameStateMachine: GameStateMachine<SpinResponse>;
  private lastSelectedButtonIndex: number = -1;
  private popupHandled = false;
  private readonly orientationHandler: CorsicaBonusOrientationHandlerComponent;
  private readonly gameDataService: GameDataService;

  constructor(
    container: Container,
    bonusScene: IBonusScene,
    configuration: IConfiguration,
    bonusContext: BonusContext,
    updateBonusFinishPropertiesWithCurrent: Func3<
      BonusResponse,
      Map<string, any>,
      Map<string, any>,
      void
    > | null,
    gameType: string,
    cancellationToken: CancellationToken
  ) {
    super(
      bonusScene,
      configuration,
      bonusContext,
      updateBonusFinishPropertiesWithCurrent,
      gameType,
      cancellationToken,
      container.resolve<INumberFormatter>(NUMBER_FORMATTER_TOKEN)
    );
    this.gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;

    this.gameDataService = container.resolve<GameDataService>(GAME_DATA_SERVICE_TOKEN);

    this.orientationHandler = new CorsicaBonusOrientationHandlerComponent();

    this.bonusScene.roundShown.listen(() => {
      this.updateOrientationHandler();
    });
  }

  start(bonusResponse: BonusResponse): void {
    void this.onResponse(bonusResponse);
  }

  public onSelectButton(selectIndex: number): void {
    this.lastSelectedButtonIndex = selectIndex;
  }

  public async sendToServer(e: SelectionArgs): Promise<void> {
    if (
      this.bonusScene.waitingUserInteraction &&
      this.bonusScene.serverIndexes().includes(this.serverIndex) &&
      this.serverIndex == e.serverIndex
    ) {
      const response = await this.gameDataService.pickBonus({
        pickedButtons: [e.selectAction == SelectAction.SendValue ? e.sendValue! : e.selectedIndex!],
        round: this.serverIndex,
        type: this.gameType,
      });
      this.gameStateMachine.responseHolder.preResponse = response;
      this.gameStateMachine.responseHolder.curResponse = response;
      this.cancellationToken.throwIfCancellationRequested();
      await this.onResponse(response.bonusInfo!);
    }
  }

  public async onResponse(bonusResponse: BonusResponse): Promise<void> {
    const popupRound = this.configuration.rounds.find((round) => round.type === 'Popup');
    if (popupRound && !this.popupHandled) {
      this.popupHandled = true;
      super.start(bonusResponse);
      this.updateOrientationHandler(); // Initial update
    } else {
      const newBonusResponse = await this.processRoundSpecificLogic(bonusResponse);
      if (!newBonusResponse) {
        return;
      }

      if (bonusResponse.bonusFinished) {
        bonusResponse.currentRound!.roundNumber = this.configuration.rounds.length;
      }

      await super.onResponse(newBonusResponse);
    }
  }

  private async processRoundSpecificLogic(
    bonusResponse: BonusResponse
  ): Promise<BonusResponseWithRules | null> {
    if (!bonusResponse.currentRound) {
      return null;
    }

    const roundNumber = bonusResponse.currentRound.roundNumber;

    switch (roundNumber) {
      case 0:
        return await this.processExplosionRound(bonusResponse);
      case 1:
        return await this.processBustedRound(bonusResponse);
      case 2:
        return await this.processDirectionRound(bonusResponse);
      default:
        console.warn(`Unknown round number: ${roundNumber}`);
        return null;
    }
  }

  private async processExplosionRound(
    bonusResponse: BonusResponse
  ): Promise<BonusResponseWithRules> {
    return {
      ...bonusResponse,
      currentRound: {
        ...bonusResponse.currentRound!,
        selectedButtons: bonusResponse.currentRound!.selectedButtons!.map((btn) => ({
          ...btn,
          index: this.lastSelectedButtonIndex,
          type: btn.value ? ExplosionRound.Boom : ExplosionRound.Fail,
        })),
        rules: {
          messageTypeLogic: {
            name: RuleTypeLogic.WinLose,
            winTypes: [ExplosionRound.Boom],
            failTypes: [ExplosionRound.Fail],
          },
          propertyGenerators: [RulePropertyGenerator.WinLoseIndexes],
        },
      },
      nextRound: {
        ...bonusResponse.nextRound!,
        rules: null,
      },
    };
  }

  private async processBustedRound(bonusResponse: BonusResponse): Promise<BonusResponseWithRules> {
    return {
      ...bonusResponse,
      currentRound: {
        ...bonusResponse.currentRound!,
        selectedButtons: bonusResponse.currentRound!.selectedButtons!.map((btn) => ({
          ...btn,
          index: this.lastSelectedButtonIndex,
          type: btn.value ? BustedRound.RunAway : BustedRound.Bust,
        })),
        rules: {
          messageTypeLogic: {
            name: RuleTypeLogic.WinLose,
            winTypes: [BustedRound.RunAway],
            failTypes: [BustedRound.Bust],
          },
          propertyGenerators: [RulePropertyGenerator.WinLoseIndexes],
        },
      },
      nextRound: {
        ...bonusResponse.nextRound!,
        rules: null,
      },
    };
  }

  private async processDirectionRound(
    bonusResponse: BonusResponse
  ): Promise<BonusResponseWithRules> {
    return {
      ...bonusResponse,
      currentRound: {
        ...bonusResponse.currentRound!,
        selectedButtons: bonusResponse.currentRound!.selectedButtons!.map((btn) => ({
          ...btn,
          index: this.lastSelectedButtonIndex,
          type: btn.value ? DirectionRound.Escaped : DirectionRound.Bust,
        })),
        rules: {
          messageTypeLogic: {
            name: RuleTypeLogic.WinLose,
            winTypes: [DirectionRound.Escaped],
            failTypes: [DirectionRound.Bust],
          },
          propertyGenerators: [RulePropertyGenerator.WinLoseIndexes],
        },
      },
      nextRound: {
        ...bonusResponse.nextRound!,
        rules: null,
      },
    };
  }

  private updateOrientationHandler(): void {
    this.orientationHandler.clearManagedElements();

    const roundConfig = this.configuration.rounds[this.bonusScene.roundIndex()];

    if (roundConfig && roundConfig.orientationManagedElements) {
      const elements = roundConfig.orientationManagedElements
        .map((id) => this.bonusScene.parent.findAllById(id))
        .reduce((acc, curr) => [...acc, ...curr], [])
        .filter((element) => !!element) as SceneObject[];
      this.orientationHandler.addManagedElements(elements);
    }
  }

  public dispose(): void {
    this.orientationHandler.dispose();
    super.dispose();
  }
}
